import * as React from "react";
import { type IColumn, Stack } from "@fluentui/react";
import { omit } from "ramda";
import { action, autorun, computed, makeObservable, observable, runInAction } from "mobx";
import {
    type VehiclesApiResource,
    type VehiclesQuery,
} from "../../domain/repositories/repository-vehicles";
import { createUuid, type UUID } from "../../utils/uuid";
import {
    type VehicleSortKey,
    type ColumnVisibility,
    type Vehicle,
    type VehicleListStats,
    VehiclesExportVehicleListListType,
} from "../../api";
import { observer } from "mobx-react";
import type { SortState } from "../../utils/sort-state";
import type { PaginationState } from "../../utils/pagination-state";
import { LayoutList } from "../layouts/layout-list";
import { ElofleetList, type ElofleetListProps } from "../atoms/elofleet-list";
import { ListVehicleProductivityItemCell } from "../atoms/list-vehicle-productivity-item-cell";
import { FilterVehicleList, type FilterVehicleListQuery } from "../organisms/filter-vehicle-list";
import type { SearchState } from "../../utils/search-state";
import { ListSearchField } from "../atoms/list-search-field";
import { DatePicker } from "../organisms/date-picker";
import { endOfISOWeek, startOfISOWeek } from "date-fns";
import { ExportButton, type ExportQuery } from "./export-button";
import { defaultPageSize } from "../../utils/constants";

export interface ListVehicleProductivityProps extends Omit<ElofleetListProps, "items" | "columns"> {
    readonly ids?: UUID[];
    readonly commandBar?: JSX.Element;
    readonly listStateId?: UUID;
}

interface VehicleListStatsItem {
    readonly columns: string[];
}

import { store } from "../../store";

@observer
export class ListVehicleProductivity extends React.Component<ListVehicleProductivityProps> {
    private _listStateId = createUuid();

    // This item is passed into the <ElofleetList /> component and displays
    // vehicle stats for defined columns summed over all vehicles inside that list.
    private vehicleListStatsItem: VehicleListStatsItem = {
        columns: ["loginTime", "driveTime", "idleTime"],
    };

    @observable private accessor filterVehicleListQuery: FilterVehicleListQuery = {
        filteredDepartments: [],
        filteredVehicleGroups: [],
        filteredProcurementTypes: [],
        isFiltered: false,
    };
    @observable private accessor vehicles: Vehicle[] | undefined;
    @observable private accessor vehicleListStats: VehicleListStats | undefined;
    @observable private accessor dateStart: Date | undefined = startOfISOWeek(new Date());
    @observable private accessor dateEnd: Date | undefined = endOfISOWeek(new Date());

    constructor(props: ListVehicleProductivityProps) {
        super(props);
        makeObservable(this);
    }

    componentDidMount(): void {
        store.serviceListStates.vehicleProductivity.initializeList(this.listStateId, {
            query: () => this.query,
        });
    }

    @computed private get listStateId(): UUID {
        return this.props.listStateId ?? this._listStateId;
    }

    @computed private get sortState(): SortState<VehicleSortKey> {
        return store.serviceListStates.vehicleProductivity.getSortState(this.listStateId);
    }

    @computed private get paginationState(): PaginationState<VehiclesApiResource> {
        return store.serviceListStates.vehicleProductivity.getPaginationState(this.listStateId);
    }

    @computed private get searchState(): SearchState {
        return store.serviceListStates.vehicleProductivity.getSearchState(this.listStateId);
    }

    @computed private get columnVisibility(): ColumnVisibility | undefined {
        return store.repositoryColumnVisibility.byQuery()[0];
    }

    @computed private get query(): VehiclesQuery {
        // We need to set the page number here which was set by the mobx repository originally.
        const page = this.paginationState.pagination.offset / defaultPageSize;
        const query = {
            pageSize: defaultPageSize,
            ...this.sortState.query,
            ...this.searchState.query,
            page,
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
        } as VehiclesQuery;
        if (this.filterVehicleListQuery === undefined) {
            // VehicleListStats could be effected by date picker only,
            // therefore fetch stats even if the filter is not set.
            return query;
        }
        const filteredDepartments = this.filterVehicleListQuery.filteredDepartments;
        if (filteredDepartments && filteredDepartments.length > 0) {
            query.departmentIds = filteredDepartments;
        }
        const filteredVehicleGroups = this.filterVehicleListQuery.filteredVehicleGroups;
        if (filteredVehicleGroups && filteredVehicleGroups.length > 0) {
            query.vehicleGroupIds = filteredVehicleGroups;
        }
        const filteredProcurementTypes = this.filterVehicleListQuery.filteredProcurementTypes;
        if (filteredProcurementTypes && filteredProcurementTypes.length > 0) {
            query.procurementTypes = filteredProcurementTypes;
        }
        return query;
    }

    @computed private get exportQuery(): ExportQuery {
        const query = Object.entries({
            list_type: VehiclesExportVehicleListListType.PRODUCTIVITY,
            ...this.query,
        });
        return { query, basePath: "/api/v1/vehicles/export?" };
    }

    private fetchVehicles(): void {
        // Fetch the vehicles every time the query changes.
        autorun(() => {
            store.vehiclesApi.vehiclesListVehicles({ ...this.query }).then((result) => {
                runInAction(() => {
                    this.vehicles = result;
                });
            });
        });
    }

    private fetchVehicleListStats(): void {
        // Fetch the vehicle list stats every time the query changes.
        autorun(() => {
            store.vehiclesApi.vehiclesReadVehicleListStats({ ...this.query }).then((result) => {
                runInAction(() => {
                    this.vehicleListStats = result;
                });
            });
        });
    }

    @computed private get columns(): IColumn[] {
        const columns: IColumn[] = [];

        if (this.columnVisibility?.vehicleProductivityList.vehicleSerialNumber) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.serialNumber.name"),
                key: "serialNumber",
                minWidth: 100,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleManufacturer) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.manufacturer.name"),
                key: "manufacturer",
                minWidth: 100,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleModel) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.model.name"),
                key: "model",
                minWidth: 100,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleTypeLabel) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.vehicleTypeLabel.name"),
                key: "vehicleType",
                minWidth: 100,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleEngineType) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.vehicleEngineType.name"),
                key: "engineType",
                minWidth: 100,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleManufactureYear) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.manufactureYear.name"),
                key: "manufactureYear",
                minWidth: 150,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.departmentLabel) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.department.name"),
                key: "department",
                minWidth: 150,
            });
        }
        if (this.columnVisibility?.vehicleProductivityList.vehicleGroupLabel) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.group.name"),
                key: "group",
                minWidth: 150,
            });
        }
        columns.push({
            name: store.i18n.t("listVehicleProductivity.column.loginTime.name"),
            key: "loginTime",
            minWidth: 150,
        });
        columns.push({
            name: store.i18n.t("listVehicleProductivity.column.driveTime.name"),
            key: "driveTime",
            minWidth: 150,
        });
        columns.push({
            name: store.i18n.t("listVehicleProductivity.column.idleTime.name"),
            key: "idleTime",
            minWidth: 150,
        });
        if (this.columnVisibility?.vehicleProductivityList.vehicleLastSynchronizedAt) {
            columns.push({
                name: store.i18n.t("listVehicleProductivity.column.lastSynchronization.name"),
                key: "lastSynchronization",
                minWidth: 150,
            });
        }

        return columns;
    }

    private renderVehicleStatsItem(columnKey: string): JSX.Element {
        let hours;
        switch (columnKey) {
            case "loginTime":
                hours = this.vehicleListStats!.totalLoginSeconds;
                break;
            case "driveTime":
                hours = this.vehicleListStats!.totalDriveSeconds;
                break;
            case "idleTime":
                hours =
                    this.vehicleListStats!.totalLoginSeconds -
                    this.vehicleListStats!.totalDriveSeconds;
                break;
            default:
                break;
        }
        if (hours === undefined) {
            return <></>;
        }
        return (
            <span>
                <strong>{store.i18n.formatDuration(hours)}</strong>
            </span>
        );
    }

    private renderItem(item: Vehicle | VehicleListStatsItem, column: IColumn): JSX.Element {
        // Check for a VehicleListStatsItem as suggested in TS docs:
        // https://www.typescriptlang.org/docs/handbook/2/narrowing.html#the-in-operator-narrowing.
        if ("columns" in item) {
            return this.renderVehicleStatsItem(column.key);
        }
        return (
            <ListVehicleProductivityItemCell
                vehicle={item}
                column={column}
                filteredVehicleGroups={this.filterVehicleListQuery.filteredVehicleGroups ?? []}
            />
        );
    }

    @action.bound public onDateRangeChanged(dateStart?: Date, dateEnd?: Date): void {
        this.dateStart = dateStart;
        this.dateEnd = dateEnd;
    }

    @action.bound private setVehicleListFilter(filterQuery: FilterVehicleListQuery): void {
        this.filterVehicleListQuery = {
            filteredDepartments: filterQuery.filteredDepartments,
            filteredVehicleGroups: filterQuery.filteredVehicleGroups,
            filteredProcurementTypes: filterQuery.filteredProcurementTypes,
            isFiltered: filterQuery.isFiltered,
        };
    }

    public render(): JSX.Element {
        if (!store.serviceListStates.vehicleProductivity.isInitialized(this.listStateId)) {
            return <></>;
        }
        if (this.vehicles === undefined) {
            this.fetchVehicles();
            return <></>;
        }
        if (this.vehicleListStats === undefined) {
            this.fetchVehicleListStats();
            return <></>;
        }
        // Forward properties that are used by Fluent UI to the `ShimmeredDetailsList`.
        const props = omit(["ids"], this.props);
        return (
            <LayoutList
                paginationState={this.paginationState}
                commandBar={
                    <Stack horizontal horizontalAlign="space-between">
                        {this.props.commandBar}
                        <ExportButton query={this.exportQuery} />
                    </Stack>
                }
                searchField={
                    <ListSearchField
                        placeholder={store.i18n.t("listVehicles.search.placeholder")}
                        listStateId={this.listStateId}
                        listState={store.serviceListStates.vehicleProductivity}
                    />
                }
                filter={
                    <Stack
                        horizontal
                        style={{ width: "100%" }}
                        horizontalAlign="end"
                        verticalAlign="center"
                        tokens={{ childrenGap: 20 }}
                    >
                        <FilterVehicleList setVehicleListFilter={this.setVehicleListFilter} />
                        <DatePicker onDateRangeChanged={this.onDateRangeChanged} />
                    </Stack>
                }
            >
                <ElofleetList
                    {...props}
                    onRenderItemColumn={(item, _index, column) => this.renderItem(item, column!)}
                    onColumnHeaderClick={this.sortState.toggleColumn}
                    columns={this.sortState.patchColumns(this.columns)}
                    // Prepend the vehicle list stats so it's rendered as the first item as we want it to
                    // appear as a summary above the other entries.
                    items={[this.vehicleListStatsItem, ...this.vehicles]}
                />
            </LayoutList>
        );
    }
}
